import React, { useState, useRef, useEffect } from "react";
import ScrollToTop from "../ScrollToTop";
import Navbar2 from "../Nav Components/Navbar2";
import Sidebar2 from "../Sidebar2";
import Footer2 from "../Footer Components/Footer2";
import ContactForm from "../ContactForm";


import CreteHero from "../images/retreat/locations/creteHero.jpg";
import GreeceHero from "../images/retreat/locations/crete.jpg";
import MoroccoHero from "../images/retreat/locations/marrakech03.jpeg";
import ItalyHero from "../images/retreat/locations/sorrento.jpeg";

import {
  PageContainer,
  HeroSection,
  HeroTitle,
  InfoSection,
  InfoText,
  BulletCardsContainer,
  BulletCard,
  CardsWrapper,
  CardsContainer,
  RetreatCard,
  CardImage,
  CardContent,
  CardTitle,
  CardDescription,
  Itinerary,
  PartnerSection,
  PartnerLabel,
  CardNav,
  NavArrow,
} from "../Retreat Page Components/RetreatElements";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";

const retreats = [
  {
    title: "CRETE",
    image: GreeceHero,
    description:
      "Crete offers a powerful mix of ancient history, dramatic landscapes, and Mediterranean calm - an ideal environment for deep learning and renewal. This Advanced Retreat combines two neuroscience-based morning workshops with immersive cultural experiences, creating space to develop focus, resilience, and team cohesion in a setting that inspires.",
    itinerary: [
      {
        day: "Day 1",
        events: [
          "Arrive in Heraklion from London and settle in with a welcome reception and buffet dinner at the hotel, setting the tone for the days ahead.",
        ],
      },
      {
        day: "Day 2",
        events: [
          "After the first morning neuroscience based training session, choose between a visit to a private olive farm with cheese-making, olive oil tasting and local cuisine, or an adventurous 4x4 treasure hunt through the hills of Crete.",
        ],
      },
      {
        day: "Day 3",
        events: [
          "Begin with your second neuroscience-based workshop, followed by your choice of restorative leisure - spa, golf, or beach. In the evening, enjoy a private boat cruise and guided tour of Spinalonga, finishing with a traditional Cretan dinner.",
        ],
      },
      {
        day: "Day 4",
        events: [
          "Spend a relaxing morning at leisure before a guided visit to Knossos Palace, followed by a farewell lunch at a local taverna and your return flight from Heraklion to London.",
        ],
      },
    ],
  },
  {
    title: "MARRAKECH",
    image: MoroccoHero,
    description:
      "Marrakech delivers vibrant contrast: colourful souks, peaceful gardens, and desert stillness. This Advanced Retreat is designed to energise and refocus leaders through two neuroscience-based morning training sessions, matched with unforgettable cultural moments and immersive dining.",
    itinerary: [
      {
        day: "Day 1",
        events: [
          "Fly from London to Marrakech and begin your journey with a welcome reception and buffet dinner at the hotel.",
        ],
      },
      {
        day: "Day 2",
        events: [
          "The day starts with a neuroscience-based workshop. In the afternoon, choose between a guided tour of the old city and souks or a visit to the tranquil Majorelle Gardens. The evening ends with group dining at the iconic Comptoir.",
        ],
      },
      {
        day: "Day 3",
        events: [
          "The day starts with the final neuroscience-based workshop. In the afternoon, head into the desert for a guided experience, followed by a magical group dinner under the stars.",
        ],
      },
      {
        day: "Day 4",
        events: [
          "Enjoy a relaxed morning before transferring to Marrakech airport for your flight back to London.",
        ],
      },
    ],
  },
  {
    title: "SORRENTO",
    image: ItalyHero,
    description:
      "Sorrento blends clifftop beauty, ancient Roman heritage, and Italian charm. This Advanced Retreat pairs neuroscience based training with curated excursions that help leaders reset, reconnect, and return with renewed clarity.",
    itinerary: [
      {
        day: "Day 1",
        events: [
          "Arrive in Naples and transfer to Sorrento, where you’ll be welcomed with a reception and buffet dinner at the hotel overlooking the Bay of Naples.",
        ],
      },
      {
        day: "Day 2",
        events: [
          "Begin the day with a neuroscience-led workshop. In the afternoon, select from a choice of immersive experiences: drive classic Fiat 500s to a monastery for wood-fired pizza, explore Pompeii or Herculaneum with a walk up Vesuvius, or take part in a local wine tasting. The group reunites in the evening for dinner together.",
        ],
      },
      {
        day: "Day 3",
        events: [
          "This day begins with the final neuroscience based workshop then the afternoon is yours to relax or explore, followed by a private sunset cruise to Capri and dinner on board.",
        ],
      },
      {
        day: "Day 4",
        events: [
          "After a leisurely morning, transfer to Naples airport for your return flight to London, closing the Advanced Retreat with a renewed sense of purpose.",
        ],
      },
    ],
  },
];

const RetreatsPage = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [current, setCurrent] = useState(0);
  const touchStartX = useRef(null);
  const cardRefs = useRef([]);
  const [containerHeight, setContainerHeight] = useState("auto");
  const toggle = () => setIsOpen(!isOpen);

  const nextCard = () => setCurrent((prev) => (prev + 1) % retreats.length);
  const prevCard = () =>
    setCurrent((prev) => (prev - 1 + retreats.length) % retreats.length);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const diff = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(diff) > 50) {
      diff < 0 ? nextCard() : prevCard();
    }
    touchStartX.current = null;
  };

  useEffect(() => {
    const updateHeight = () => {
      const currentCard = cardRefs.current[current];
      if (currentCard) {
        setContainerHeight(currentCard.offsetHeight);
      }
    };

    updateHeight();

    if (typeof window !== "undefined") {
      window.addEventListener("resize", updateHeight);
    }

    let resizeObserver;
    const currentCard = cardRefs.current[current];
    if (typeof ResizeObserver !== "undefined" && currentCard) {
      resizeObserver = new ResizeObserver(() => updateHeight());
      resizeObserver.observe(currentCard);
    }

    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("resize", updateHeight);
      }
      if (resizeObserver && currentCard) {
        resizeObserver.disconnect();
      }
    };
  }, [current]);

  return (
    <>
      <Sidebar2 isOpen={isOpen} toggle={toggle} />
      <ScrollToTop />
      <Navbar2 toggle={toggle} />
      <PageContainer>
        <HeroSection $bg={CreteHero}>
          <HeroTitle>Retreats</HeroTitle>
        </HeroSection>
        <InfoSection>
          <InfoText>
            <h2>Advanced Leadership</h2>
            <p>
              These are not leadership retreats, they are a leadership advance.
            </p>
            <p>
              We’ve created an immersive, highly engaging experience that blends cutting-edge neuroscience with real-time data analytics. Delivered in exceptional locations worldwide, this programme supports leaders navigating fast-paced, complex change - with proven results.
            </p>
            <p>
            Our research, using brain-computer interface (BCI) technology, shows that Focus Improvement Techniques (F.I.T) drive measurable gains in both performance and wellbeing. Based on findings from our flagship programme, this ‘advanced leadership retreat’ is designed for executives, teams, and organisations going through change. They are designed for those who want results and who want to know how wellbeing affects performance.  
            </p>
          </InfoText>
          <BulletCardsContainer>
            <BulletCard>
              <h3>Measured Benefits</h3>
              <ul>
                <li>Sustained attention +53%</li>
                <li>Recovery time -55%</li>
                <li>Decision accuracy +51.4%</li>
              </ul>
            </BulletCard>
            <BulletCard>
              <h3>Programme Features</h3>
              <ul>
                <li>Morning “change” workshops based on the latest research in neuroscience</li>
                <li>Real-time performance feedback + DASS-21 reports</li>
                <li>Curated cultural and wellness experiences to spark recovery, creativity, and perspective</li>
              </ul>
            </BulletCard>
          </BulletCardsContainer>
          <InfoText>
            <h3>Our Delivery Team</h3>
            <p>
              These advanced retreats are expertly managed by our experienced travel partner Caroline Dunn.
              Our registered psychology partner oversees DASS-21 assessments and reporting. And finally, Ellen, our founder and licensed NCS consultant, leads the full F.I.T programme, including the four-day retreat.
            </p>
          </InfoText>
        </InfoSection>
        <CardsWrapper>
          <CardNav>
            <NavArrow onClick={prevCard} aria-label="Previous">
              <FaChevronLeft />
            </NavArrow>
            <NavArrow onClick={nextCard} aria-label="Next">
              <FaChevronRight />
            </NavArrow>
          </CardNav>
          <CardsContainer
            style={{ height: containerHeight }}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            {retreats.map((retreat, index) => {
              let position = "";
              if (index === current) position = "active";
              else if (index === (current + 1) % retreats.length)
                position = "next";
              else if (index === (current - 1 + retreats.length) % retreats.length)
                position = "prev";
              return (
                <RetreatCard
                  key={retreat.title}
                  className={position}
                  ref={(el) => (cardRefs.current[index] = el)}
                >
                  <CardImage src={retreat.image} alt={retreat.title} />
                  <CardContent>
                    <CardTitle>{retreat.title}</CardTitle>
                    <CardDescription>{retreat.description}</CardDescription>
                    <Itinerary>
                      {retreat.itinerary.map((item, idx) => (
                        <li key={idx}>
                          <strong>{item.day}</strong>
                          <ul>
                            {item.events.map((event, eIdx) => (
                              <li key={eIdx}>{event}</li>
                            ))}
                          </ul>
                        </li>
                      ))}
                    </Itinerary>
                    <PartnerSection>
                      <PartnerLabel>Travel partner</PartnerLabel>
                    </PartnerSection>
                  </CardContent>
                </RetreatCard>
              );
            })}
          </CardsContainer>
        </CardsWrapper>
      </PageContainer>
      <ContactForm
        isFilterActive={false}
        emailOnly
        description="Enter your email address and a member of the team will be in touch."
        highlightCard
      />
      <Footer2 />
    </>
  );
};

export default RetreatsPage;
